import { Anchor, Button, Grid, Group, TagsInput, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import DatePicker from '../../components/DatePicker';
import ErrorMessage from '../../components/ErrorMessage';
import prettifiedLabel from '../../constants/labelPrettifier';
import useFetch from '../../helpers/useFetch';

const ProfilesForm = ({ trackingId, profileData }) => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const [formData, setFormData] = useState(
    profileData || {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      registeredAt: null,
      subscribedAt: undefined,
      birthday: null,
      subscriptions: [],
      tags: [],
    }
  );

  useEffect(() => {
    if (profileData) {
      setFormData(profileData);
    }
  }, [profileData]);

  const [error, setError] = useState();
  const { accountId, domainId } = useParams();

  const parseDate = (name, value) => {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        setError({ message: `Please provide valid date for ${prettifiedLabel(name)} or leave it empty!` });
        return null;
      }

      const offset = new Date().getTimezoneOffset();
      date.setMinutes(date.getMinutes() - offset);

      return date.toISOString();
    } catch {
      setError({ message: `Please provide valid date for ${prettifiedLabel(name)} or leave it empty!` });
      return null;
    }
  };
  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'birthday' || name === 'subscribedAt' || name === 'registeredAt') {
      const parsedValue = parseDate(name, value);

      if (parsedValue === null) {
        return;
      } else {
        setError(null);
      }

      setFormData((prevFormData) => ({ ...prevFormData, [name]: parsedValue }));
    } else {
      // Editing the field the API flagged as taken clears the conflict, so the red field and its
      // link do not linger over a value the admin has already changed.
      if (error?.conflict?.field === name) {
        setError(null);
      }

      setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError(null);

    const url = `/api/admin/accounts/${accountId}/domains/${domainId}/profiles`;

    if (profileData) {
      fetch(`${url}/${encodeURIComponent(trackingId)}`, {
        method: 'PUT',
        body: JSON.stringify(formData),
      })
        .then((response) => {
          if (response.result === 'updated') {
            navigate(`/accounts/${accountId}/domains/${domainId}/profiles/${encodeURIComponent(trackingId)}/events`);
          }
        })
        .catch(setError);
      return;
    }

    fetch(url, {
      method: 'POST',
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.result === 'created') {
          navigate(`/accounts/${accountId}/domains/${domainId}/profiles`);
        }
      })
      .catch(setError);
  };

  // An email or phone number lives on exactly one profile per domain, so the API refuses a save
  // that would move one off another profile and answers with `conflict` describing the profile
  // that holds it. Link straight to that profile (in a new tab, so the half-filled form survives)
  // - identifying the other record by hand used to mean searching the profiles list by hand.
  const conflict = error?.conflict;
  const conflictHref = conflict?.profileId
    ? `#/accounts/${accountId}/domains/${domainId}/profiles/${encodeURIComponent(conflict.profileId)}/events`
    : undefined;
  const conflictFieldError = (field) => (conflict?.field === field ? 'Already used by another profile' : undefined);

  return (
    <div style={{ width: '100%' }}>
      {error && (
        <div style={{ marginBottom: 10 }}>
          <ErrorMessage message={error.message}>
            {conflictHref && (
              <>
                {' '}
                <Anchor href={conflictHref} target="_blank" rel="noreferrer" c="red" underline="always">
                  View that profile{conflict.email ? ` (${conflict.email})` : ''}
                </Anchor>
              </>
            )}
          </ErrorMessage>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>First name</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TextInput name="firstName" value={formData.firstName} placeholder="First name" onChange={handleChange} />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Last name</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TextInput name="lastName" value={formData.lastName} placeholder="Last name" onChange={handleChange} />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Email *</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TextInput
              name="email"
              type="email"
              value={formData.email}
              placeholder="Email"
              onChange={handleChange}
              error={conflictFieldError('email')}
              required
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Phone number</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TextInput
              name="phoneNumber"
              value={formData.phoneNumber}
              placeholder="Phone number"
              onChange={handleChange}
              error={conflictFieldError('phoneNumber')}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Registration date</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <DatePicker
              name="registeredAt"
              size="sm"
              placeholder="Registration date"
              maxDate={new Date()}
              value={formData.registeredAt}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChange={(value) => handleChange({ target: { name: 'registeredAt', value: value || null } })}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Subscribe date</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <DatePicker
              name="subscribedAt"
              size="sm"
              placeholder="Subscribe date"
              maxDate={new Date()}
              value={formData.subscribedAt}
              disabled={trackingId}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChange={(value) => handleChange({ target: { name: 'subscribedAt', value: value || null } })}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Date of Birth</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <DatePicker
              name="birthday"
              size="sm"
              placeholder="Date of Birth"
              maxDate={new Date()}
              value={formData.birthday}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChange={(value) => handleChange({ target: { name: 'birthday', value } })}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Subscriptions</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TagsInput
              data={[]}
              onChange={(subscriptions) => handleChange({ target: { name: 'subscriptions', value: subscriptions } })}
              value={formData.subscriptions || []}
              placeholder="Add subscriptions"
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Tags</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TagsInput
              data={[]}
              onChange={(tags) => handleChange({ target: { name: 'tags', value: tags } })}
              value={formData.tags || []}
              placeholder="Add tags"
            />
          </Grid.Col>
        </Grid>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button type="submit" leftSection={<IconDeviceFloppy size={16} />}>
            Save
          </Button>
        </Group>
      </form>
    </div>
  );
};

export default ProfilesForm;
